import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiLoading, mdiMagnify, mdiPen, mdiPlus, mdiTrashCan } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import { FormEmpresa } from './FormEmpresa';
import Grid, { GridItem } from '../../components/layout/Grid';
import { format, parseISO } from 'date-fns';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

const Page = () => {
  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string> | null>(null);

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const queryClient = useQueryClient();

  const headers: DataTableHeader[] = React.useMemo(
    () => [
      { label: 'Razão Social', width: '30%', column: 'razao_social' },
      { label: 'Nome Fantasia', width: '30%', column: 'nome_fantasia' },
      { label: 'CNPJ', width: '15%', column: 'cnpj' },
      {
        label: 'Criado em',
        width: '15%',
        render: (row: any) =>
          format(parseISO(row.data_criacao), 'dd/MM/yyyy HH:mm'),
      },
      {
        label: 'Ações',
        width: '100px',
        stickRight: true,
        alignRight: true,
        render: (row: any) => {
          return (
            <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
              <Botao
                title="Editar"
                variant="none-primary"
                icon={mdiPen}
                onClick={() => handleEdit(row)}
              />
              <BotaoPerigoso
                variant="none-danger"
                icon={mdiTrashCan}
                onClick={() => handleDelete(row)}
                buttonProps={{
                  title: 'Apagar'
                }}
              />
            </HBox>
          );
        },
      },
    ],
    []
  );

  const queryList = useInfiniteQuery(
    ['empresa', { search: debouncedSearch }],
    async ({ pageParam = null }) => {
      const params: Record<string, string> = {};
      if (pageParam) params.cursor = pageParam;
      if (debouncedSearch) params.q = debouncedSearch;

      const result = await axios.get('/api/empresa', { params });
      return result;
    },
    {
      getNextPageParam: (lastPage: any) => {
        if (!lastPage?.data?.cursor) return undefined;
        return lastPage?.data?.cursor;
      },
    }
  );

  const queryFlatList = queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total = queryList.data?.pages?.[0]?.data?.total ?? 0;

  const saveObjMutator = useMutation(
    async () => {
      setFieldErrors(null);

      if (!editObject.razao_social?.trim()) {
        setFieldErrors({ razao_social: 'Campo obrigatório' });
        return;
      }

      const url = editObject.id ? `/api/empresa/${editObject.id}` : '/api/empresa';
      const method = editObject.id ? 'put' : 'post';

      const result = await axios[method](url, editObject);
      return result;
    },
    {
      onSuccess: () => {
        toast.success(
          editObject.id ? 'Empresa atualizada com sucesso!' : 'Empresa criada com sucesso!'
        );
        handleCloseModal();
        queryList.refetch();
        queryClient.invalidateQueries("empresa");
      },
      onError: (error: any) => {
        const data = error.response?.data;
        if (data?.fieldErrors && typeof data.fieldErrors === 'object') {
          setFieldErrors(data.fieldErrors);
        } else {
          toast.error(data?.msg || 'Erro ao salvar empresa');
        }
      },
    }
  );

  const deleteObjMutator = useMutation(
    async (id: number) => {
      const result = await axios.delete(`/api/empresa/${id}`);
      return result;
    },
    {
      onSuccess: () => {
        toast.success('Empresa excluída com sucesso!');
        queryList.refetch();
        queryClient.invalidateQueries("empresa");
      },
      onError: error => {
        toast.error('Erro ao excluir empresa');
      },
    }
  );

  const handleCloseModal = useCallback(() => {
    setEditObject(null);
    setFieldErrors(null);
  }, []);

  const handleOpenModalNewObject = useCallback(() => {
    setEditObject({});
    setFieldErrors(null);
  }, []);

  const handleEdit = useCallback((obj: any) => {
    setEditObject({ ...obj });
    setFieldErrors(null);
  }, []);

  const handleDelete = useCallback((obj: any) => {
    deleteObjMutator.mutate(obj.id);
  }, [deleteObjMutator]);

  const handleChange = useCallback(() => {
    saveObjMutator.mutate();
  }, [saveObjMutator, editObject]);

  const editMode = !!editObject?.id;

  return (
    <LayoutUsuario>
      <Modal
        instanceId='modal-empresa'
        title={editMode ? 'Editar empresa' : 'Nova empresa'}
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={handleChange}
        confirmProps={{
          disabled: saveObjMutator.isLoading,
        }}
        cancelProps={{
          disabled: saveObjMutator.isLoading,
        }}
      >
        <FormEmpresa
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors as any}
        />
      </Modal>

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <Grid>
          <GridItem sm={12} md={6}>
            <VBox>
              <Titulo>Empresas</Titulo>
              <Descricao>
                Gerencie suas empresas aqui
              </Descricao>
            </VBox>
          </GridItem>
          <GridItem sm={12} md={6}>
            <HBox gap="12px" className={styles.headerAcoes}>
              <Botao
                variant="primary"
                icon={mdiPlus}
                onClick={handleOpenModalNewObject}
              >
                Nova
              </Botao>
            </HBox>
          </GridItem>
        </Grid>

        {/* Filtros */}
        <HBox>
          <CampoTexto
            prepend={<Icon path={mdiMagnify} size={0.8} color="var(--tc-color-gray-500)" />}
            placeholder="Pesquisar"
            value={search}
            onChange={setSearch}
          />
        </HBox>

        {queryList.isFetching && (
          <div>
            Aguarde...
          </div>
        )}
        {!queryList.isFetching && (
          <div>
            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
            resultados.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={queryFlatList ?? []}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default Page;
